import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Checkbox } from 'ui-components/components/checkbox';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { BULK_ALERT_MODAL_PENDO, SenderPrepay } from './constants';
import { createUserSettingPrepayDutyAlert } from './helpers';
import { bulkAlertModalMessages } from './messages';
import { AvailableDutiesOptionsProptype } from './prop-types';
import style from './style.module.less';
import { EDITOR_TYPE_SHIP_TO_COUNTRY } from '../../components/filter-editor/constants/filter-editor-type';
import { generateFilterSetWithEditorTypeIDs, buildFilterSetForSave } from '../../components/filter-editor/helpers/filter-creation-helpers';
import FormattedCurrencyContainer from '../../components/formatted-currency/container';
import ModalFooterWithActions from '../../components/modal-footer-actions';
import ModalPortal from '../../components/modal-portal';
import { addRule, fetchAutomationRules } from '../../pages/settings/api/automation-rules';
import { ACTION_TYPE_SET_PREPAY_DUTIES_AND_TAXES } from '../../pages/settings/automation-rules/constants/action-types';
import { filterActiveRulesWithRuleActions, generateNewAutomationRule } from '../../pages/settings/automation-rules/services';
import * as API from '../../store/bulk/update/bulk.update.api';
import Global from '../../store/global-selectors';
import { postFilterSet } from '../../store/saved-filters-api';
import * as UserActions from '../../store/user/user.actions';
import * as UserAPI from '../../store/user/user.api';

const AutomationRuleCreationFailureMessage = {
  notificationType: NOTIFICATION_TYPE_ERROR,
  title: <FormattedMessage {...bulkAlertModalMessages.failedToCreateAutomationRuleTitle} />,
  content: '',
  duration: 3000,
};
const AutomationRuleCreationSuccessMessage = {
  notificationType: NOTIFICATION_TYPE_SUCCESS,
  title: <FormattedMessage {...bulkAlertModalMessages.successfullyCreatedAutomationRuleTitle} />,
  content: '',
  duration: 3000,
};

const orderRuleAction = {
  actionTypeId: ACTION_TYPE_SET_PREPAY_DUTIES_AND_TAXES,
  parameter: SenderPrepay,
};

const BulkPrepayDutyAlertModal = ({
  availableDutiesOptions,
  fulfillmentPlanIds,
  fulfillmentPlanSetId,
  modalFrameworkMethods,
  postAcceptanceAction,
}) => {
  const dispatch = useDispatch();
  const { closeModal } = modalFrameworkMethods;
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);

  const [checkedPrepayDuties, setCheckedPrepayDuties] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [checkedAutomationRuleCreation, setCheckedAutomationRuleCreation] = useState(false);
  const [hasDDPAutomationRule, setHasDDPAutomationRule] = useAsyncSafeState(false);

  useEffect(() => {
    fetchAutomationRules()
      .then((rules) => {
        const filteredRules = filterActiveRulesWithRuleActions(rules, orderRuleAction);
        setHasDDPAutomationRule(Boolean(filteredRules.length));
      })
      .catch(window.console.log);
  }, [setHasDDPAutomationRule]);

  const createAutomationRuleAsync = useCallback(async () => {
    const editorTypeId = EDITOR_TYPE_SHIP_TO_COUNTRY;
    const editorTypeValues = ['CA'];
    const generatedFilterSet = generateFilterSetWithEditorTypeIDs(editorTypeId, editorTypeValues);
    const filterSetForSave = buildFilterSetForSave({
      filterSet: generatedFilterSet,
      sellerHomeCountry,
    });
    try {
      const { orderFilterSetId } = await postFilterSet(filterSetForSave);
      const automationRuleName = '[AutoGen] DDP - Prepay Duties';
      const automationRuleToSave = generateNewAutomationRule({
        name: automationRuleName,
        orderRuleActions: [orderRuleAction],
        filterId: orderFilterSetId,
      });
      addRule(automationRuleToSave)
        .then(() => NotificationService.showNotification(AutomationRuleCreationSuccessMessage))
        .catch(() => NotificationService.showNotification(AutomationRuleCreationFailureMessage));
    } catch {
      NotificationService.showNotification(AutomationRuleCreationFailureMessage);
    }
  }, [sellerHomeCountry]);

  const createAndDismissSetting = useSelector((state) =>
    createUserSettingPrepayDutyAlert(state, {
      createdPrepayDutyOn: moment().toISOString(),
      dismissedOn: moment().toISOString(),
    }),
  );
  const dismissSetting = useSelector((state) =>
    createUserSettingPrepayDutyAlert(state, {
      dismissedOn: moment().toISOString(),
    }),
  );

  const handleOnClose = useCallback(() => {
    closeModal();
    dispatch(postAcceptanceAction);
  }, [dispatch, closeModal, postAcceptanceAction]);

  const handleSaveUserSetting = useCallback(
    async (setting) => {
      try {
        await UserAPI.saveUserSettingPromise(setting);
        dispatch(UserActions.saveUserSettingSuccess(setting));
      } catch {
        dispatch(UserActions.updateUserSetting(setting));
      } finally {
        handleOnClose();
      }
    },
    [dispatch, handleOnClose],
  );

  const handleCheckedPrepayDuties = useCallback(async () => {
    try {
      const {
        data: { bulkUpdateJobGuid },
      } = await API.bulkGetBulkJobData({
        fulfillmentPlanIds,
        fulfillmentPlanSetId,
      });
      const senderPrepay = availableDutiesOptions.find(({ additionalOptionValue }) => additionalOptionValue.prepaidDuty === SenderPrepay);
      const request = {
        bulkUpdateJobGuid,
        shippingConfiguration: {
          options: {
            additionalOptions: [senderPrepay],
          },
        },
      };
      await API.updateBulkShipping(request);
      handleSaveUserSetting(createAndDismissSetting);
    } catch {
      setShowError(true);
      setIsSubmitting(false);
    }
  }, [availableDutiesOptions, createAndDismissSetting, fulfillmentPlanIds, fulfillmentPlanSetId, handleSaveUserSetting]);

  const handleOnContinue = useCallback(() => {
    setShowError(false);
    setIsSubmitting(true);
    if (checkedPrepayDuties) {
      if (checkedAutomationRuleCreation) {
        createAutomationRuleAsync();
      }
      handleCheckedPrepayDuties();
    } else {
      handleSaveUserSetting(dismissSetting);
    }
  }, [
    checkedPrepayDuties,
    dismissSetting,
    handleCheckedPrepayDuties,
    handleSaveUserSetting,
    checkedAutomationRuleCreation,
    createAutomationRuleAsync,
  ]);

  const handleModalPortalOnClose = useCallback(
    (_, reason) => {
      if (reason === 'closeButtonClick') {
        closeModal();
      }
    },
    [closeModal],
  );

  const totalCount = availableDutiesOptions.reduce((acc, option) => {
    return acc + option?.count;
  }, 0);

  const sixNinetyFiveOption = availableDutiesOptions.find((option) => option.price.value === 7.95) || {};
  const { count: sixNinetyFiveCount = 0, price: sixNinetyFivePrice = {} } = sixNinetyFiveOption;
  const nineNinetyFiveOption = availableDutiesOptions.find((option) => option.price.value === 9.95) || {};
  const { count: nineNinetyFiveCount = 0, price: nineNinetyFivePrice = {} } = nineNinetyFiveOption;

  const getCheckboxMessage = () => {
    if (totalCount === sixNinetyFiveCount) {
      return (
        <FormattedMessage
          {...bulkAlertModalMessages.prepayDutiesCheckboxSingleCase}
          values={{
            count: sixNinetyFiveCount,
            ddpPrice: <FormattedCurrencyContainer currencyCode={sixNinetyFivePrice?.code} value={sixNinetyFivePrice?.value} />,
          }}
        />
      );
    }
    if (totalCount === nineNinetyFiveCount) {
      return (
        <FormattedMessage
          {...bulkAlertModalMessages.prepayDutiesCheckboxSingleCase}
          values={{
            count: nineNinetyFiveCount,
            ddpPrice: <FormattedCurrencyContainer currencyCode={nineNinetyFivePrice?.code} value={nineNinetyFivePrice?.value} />,
          }}
        />
      );
    }
    return (
      <FormattedMessage
        {...bulkAlertModalMessages.prepayDutiesCheckboxMulitpleCase}
        values={{
          count1: sixNinetyFiveCount,
          ddpPrice1: <FormattedCurrencyContainer currencyCode={sixNinetyFivePrice?.code} value={sixNinetyFivePrice?.value} />,
          count2: nineNinetyFiveCount,
          ddpPrice2: <FormattedCurrencyContainer currencyCode={nineNinetyFivePrice?.code} value={nineNinetyFivePrice?.value} />,
        }}
      />
    );
  };
  const checkboxMessage = getCheckboxMessage();
  return (
    <ModalPortal onClose={handleModalPortalOnClose}>
      <Modal size='medium'>
        <ModalHeader>
          <FormattedMessage {...bulkAlertModalMessages.title} />
        </ModalHeader>
        <ModalBody className={style['prepay-duty-alert-modal__modal-body']}>
          {showError ? (
            <BannerMessageV2
              content={
                <FormattedMessage
                  {...bulkAlertModalMessages.errorMessage}
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              }
              status={BannerStatus.Error}
            />
          ) : null}
          <FormattedMessage
            {...bulkAlertModalMessages.message}
            values={{
              count: totalCount,
              p: (chunks) => <p>{chunks}</p>,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
          <Checkbox
            checked={checkedPrepayDuties}
            className={style['bulk-alert-modal__checkbox']}
            label={checkboxMessage}
            onChange={setCheckedPrepayDuties}
            pendoTrackingId={BULK_ALERT_MODAL_PENDO.prepayDutiesCheckbox}
          />
          <Checkbox
            checked={checkedAutomationRuleCreation}
            className={style['bulk-alert-modal__checkbox']}
            disabled={!checkedPrepayDuties || hasDDPAutomationRule}
            label={<FormattedMessage {...bulkAlertModalMessages.autoPrepayDutiesCheckbox} />}
            onChange={setCheckedAutomationRuleCreation}
            pendoTrackingId={BULK_ALERT_MODAL_PENDO.automationCheckbox}
          />
        </ModalBody>
        <ModalFooterWithActions
          actionMessage={<FormattedMessage {...bulkAlertModalMessages.continueButton} />}
          actionPendoTrackingId={BULK_ALERT_MODAL_PENDO.continueButton}
          actionTheme='primary'
          cancelMessage={<FormattedMessage {...bulkAlertModalMessages.cancelButton} />}
          isActionDisabled={isSubmitting}
          onAction={handleOnContinue}
          onCancel={closeModal}
        />
      </Modal>
    </ModalPortal>
  );
};

BulkPrepayDutyAlertModal.propTypes = {
  availableDutiesOptions: AvailableDutiesOptionsProptype.isRequired,
  fulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string),
  fulfillmentPlanSetId: PropTypes.string,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  postAcceptanceAction: PropTypes.shape({
    andPrint: PropTypes.bool,
  }).isRequired,
};

export default BulkPrepayDutyAlertModal;
